import { Category } from './Category';
import { Content } from './Content';
import { MergeMissingResolve, MergeTypeMismatchResolve } from './MergeTypes';

export type SelectiveMergeBaseline = 'left' | 'right' | 'intersection';

export type SelectiveMergeSourceSide = 'a' | 'b';

export type CompareNodeStatus =
  | 'match'
  /** Branch pairs align, but at least one direct child does not. */
  | 'has-child-diff'
  | 'missing-content'
  /** Paired category; this side contains every row on the other plus additional content. */
  | 'has-extra-content'
  | 'left-only'
  | 'right-only'
  | 'type-conflict'
  /** Type-conflict merge was applied into this side; the other side may still differ. */
  | 'type-conflict-resolved'
  /** Child exists on one side only under a parent with branch/content type conflict. */
  | 'unpaired';

export interface DocumentCompareSide {
  label: string;
  documentName: string;
  categoryId: string;
  readAccessGrantId?: number;
}

/** Which document(s) already received a Keep merge for a type-conflict row (keyed by child path). */
export type TypeConflictMergeTarget = 'left' | 'right';

/** One reversible compare copy (content rows and/or categories created on the target document). */
export interface CompareCopyUndoEntry {
  kind: 'copy';
  targetDocumentName: string;
  createdContentIds: string[];
  createdCategoryIds: string[];
}

/** Restores a content row deleted from compare (undo re-creates the row). */
export interface CompareDeleteContentUndoEntry {
  kind: 'delete-content';
  targetDocumentName: string;
  content: Content;
  categoryId: string;
  index: number;
}

/** Restores a category subtree deleted from compare. */
export interface CompareDeleteCategoryUndoEntry {
  kind: 'delete-category';
  targetDocumentName: string;
  category: Category;
  parentId: string | null;
  parentName: string | null;
  index: number;
}

export type CompareUndoEntry =
  | CompareCopyUndoEntry
  | CompareDeleteContentUndoEntry
  | CompareDeleteCategoryUndoEntry;

export function isCompareCopyUndoEntry(entry: CompareUndoEntry): entry is CompareCopyUndoEntry {
  return entry.kind === 'copy';
}

export interface DocumentCompareSession {
  tabId: string;
  left: DocumentCompareSide;
  right: DocumentCompareSide;
  leftRoot: Category;
  rightRoot: Category;
  resolveMissing: MergeMissingResolve;
  resolveTypeMismatch: MergeTypeMismatchResolve;
  typeConflictMergedSides?: Record<string, TypeConflictMergeTarget[]>;
  /** Stack of compare actions that can be undone from the tab (newest last). */
  copyUndoStack?: CompareUndoEntry[];
}

/** Selective merge workspace: result document vs one active source (toggle for intersection). */
export interface SelectiveMergeSession {
  tabId: string;
  baseline: SelectiveMergeBaseline;
  result: DocumentCompareSide;
  resultRoot: Category;
  /** Original compare (left) document. */
  sideA: DocumentCompareSide;
  sideB: DocumentCompareSide;
  sideARoot: Category;
  sideBRoot: Category;
  /** Which side is shown in the source column when baseline is intersection. */
  activeSourceSide: SelectiveMergeSourceSide;
  resolveMissing: MergeMissingResolve;
  resolveTypeMismatch: MergeTypeMismatchResolve;
  typeConflictMergedSides?: Record<string, TypeConflictMergeTarget[]>;
  copyUndoStack?: CompareUndoEntry[];
}

export function createSelectiveMergeTabId(): string {
  return `selective-merge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isSelectiveMergeTabId(tabId: string): boolean {
  return tabId.startsWith('selective-merge-');
}

export function selectiveMergeTabLabel(
  resultLabel: string,
  sourceALabel: string,
  sourceBLabel: string,
  baseline: SelectiveMergeBaseline,
): string {
  switch (baseline) {
    case 'left':
      return `Compare ${resultLabel} ← ${sourceBLabel}`;
    case 'right':
      return `Compare ${resultLabel} ← ${sourceALabel}`;
    case 'intersection':
    default:
      return `Compare ${resultLabel} ← (${sourceALabel}, ${sourceBLabel})`;
  }
}

/** User-facing compare workspace: two editable docs, view-only source, or new-document build. */
export type CompareWorkspaceKind = 'two-way' | 'view-only-source' | 'new-document';

export function classifyCompareWorkspace(
  isNewDocumentWorkspace: boolean,
  rightReadAccessGrantId?: number | null,
): CompareWorkspaceKind {
  if (isNewDocumentWorkspace) {
    return 'new-document';
  }
  if (rightReadAccessGrantId != null) {
    return 'view-only-source';
  }
  return 'two-way';
}

export function compareWorkspaceModeChipLabel(
  kind: CompareWorkspaceKind,
  targetDocumentLabel?: string,
): string {
  switch (kind) {
    case 'two-way':
      return 'Both editable';
    case 'view-only-source':
      return 'View-only source';
    case 'new-document':
      return targetDocumentLabel?.trim()
        ? `Target document: ${targetDocumentLabel.trim()}`
        : 'Target document';
  }
}

/** True when an owned compare side (not a view-only grant) is the deleted library document. */
export function ownedDocumentSideMatchesDeleted(
  side: DocumentCompareSide,
  deletedDocumentId: string,
  deletedDocumentName: string,
): boolean {
  if (side.readAccessGrantId != null) {
    return false;
  }
  const documentKey = side.documentName?.trim();
  if (!documentKey) {
    return false;
  }
  return documentKey === deletedDocumentId || documentKey === deletedDocumentName;
}

export function compareSessionUsesDeletedDocument(
  session: DocumentCompareSession,
  deletedDocumentId: string,
  deletedDocumentName: string,
): boolean {
  return (
    ownedDocumentSideMatchesDeleted(session.left, deletedDocumentId, deletedDocumentName) ||
    ownedDocumentSideMatchesDeleted(session.right, deletedDocumentId, deletedDocumentName)
  );
}

export function selectiveMergeSessionUsesDeletedDocument(
  session: SelectiveMergeSession,
  deletedDocumentId: string,
  deletedDocumentName: string,
): boolean {
  return (
    ownedDocumentSideMatchesDeleted(session.result, deletedDocumentId, deletedDocumentName) ||
    ownedDocumentSideMatchesDeleted(session.sideA, deletedDocumentId, deletedDocumentName) ||
    ownedDocumentSideMatchesDeleted(session.sideB, deletedDocumentId, deletedDocumentName)
  );
}

export function selectiveMergeFixedSourceSide(
  baseline: SelectiveMergeBaseline,
): SelectiveMergeSourceSide | null {
  if (baseline === 'left') {
    return 'b';
  }
  if (baseline === 'right') {
    return 'a';
  }
  return null;
}

export function selectiveMergeActiveSourceSide(session: SelectiveMergeSession): SelectiveMergeSourceSide {
  const fixed = selectiveMergeFixedSourceSide(session.baseline);
  return fixed ?? session.activeSourceSide;
}

export function selectiveMergeSourceSideInfo(session: SelectiveMergeSession): DocumentCompareSide {
  return selectiveMergeActiveSourceSide(session) === 'a' ? session.sideA : session.sideB;
}

export function selectiveMergeSourceRoot(session: SelectiveMergeSession): Category {
  return selectiveMergeActiveSourceSide(session) === 'a' ? session.sideARoot : session.sideBRoot;
}

export function buildSelectiveMergeDisplaySession(session: SelectiveMergeSession): DocumentCompareSession {
  return {
    tabId: session.tabId,
    left: session.result,
    right: selectiveMergeSourceSideInfo(session),
    leftRoot: session.resultRoot,
    rightRoot: selectiveMergeSourceRoot(session),
    resolveMissing: session.resolveMissing,
    resolveTypeMismatch: session.resolveTypeMismatch,
    typeConflictMergedSides: session.typeConflictMergedSides,
    copyUndoStack: session.copyUndoStack,
  };
}

export interface ContentCompareBuckets {
  both: Content[];
  leftOnly: Content[];
  rightOnly: Content[];
}

export function createDocumentCompareTabId(): string {
  return `compare-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isDocumentCompareTabId(tabId: string): boolean {
  return tabId.startsWith('compare-');
}

export function documentCompareTabLabel(leftLabel: string, rightLabel: string): string {
  return `Compare: ${leftLabel} ↔ ${rightLabel}`;
}

export type MergeCompareTypeConflictDirection = 'left' | 'right';

export interface MergeCompareCategorySubtreeTarget {
  mergeIntoCategoryId: string;
  sourceCategoryId: string;
  treatIntoAsParent: boolean;
  /** Copy all missing content explicitly (content-category merge); skips Missing/Type mismatch settings. */
  explicitCopy?: boolean;
}

export interface MergeCompareContentSelection {
  mergeCategorySubtreeToLeft?: MergeCompareCategorySubtreeTarget;
  mergeCategorySubtreeToRight?: MergeCompareCategorySubtreeTarget;
  copyToLeft?: {
    targetCategoryId?: string;
    targetParentCategoryId?: string;
    sourceContentIds?: string[];
    sourceCategoryIds?: string[];
    /** Create an empty branch shell on the target before copying sourceCategoryIds into it. */
    createBranchShell?: { parentCategoryId: string; name: string };
    /** Category created in the compare UI immediately before this copy (single-sided flow). */
    newlyCreatedCategoryId?: string;
  };
  copyToRight?: {
    targetCategoryId?: string;
    targetParentCategoryId?: string;
    sourceContentIds?: string[];
    sourceCategoryIds?: string[];
    createBranchShell?: { parentCategoryId: string; name: string };
    newlyCreatedCategoryId?: string;
  };
  typeConflict?: {
    direction: MergeCompareTypeConflictDirection;
    rows: {
      rowKey: string;
      leftCategoryId: string;
      rightCategoryId: string;
    }[];
  };
}

export function applyTypeConflictMergeRecord(
  existing: Record<string, TypeConflictMergeTarget[]> | undefined,
  rowKey: string,
  direction: MergeCompareTypeConflictDirection,
): Record<string, TypeConflictMergeTarget[]> {
  const merged = { ...(existing ?? {}) };
  const sides = new Set(merged[rowKey] ?? []);
  sides.add(direction);
  merged[rowKey] = Array.from(sides);
  return merged;
}

export function typeConflictMergeTargetsForRows(
  mergedSides: Record<string, TypeConflictMergeTarget[]> | undefined,
  rows: { key: string }[],
): { canLeft: boolean; canRight: boolean } {
  if (rows.length === 0) {
    return { canLeft: false, canRight: false };
  }
  let canLeft = true;
  let canRight = true;
  for (const row of rows) {
    const done = mergedSides?.[row.key] ?? [];
    if (done.includes('left')) {
      canLeft = false;
    }
    if (done.includes('right')) {
      canRight = false;
    }
  }
  return { canLeft, canRight };
}
